import type { User } from "@supabase/supabase-js"

import {
  buildSharedAuthTestAccessToken,
  getSharedAuthTestUser,
  isPlaywrightAuthE2EEnabled,
  PLAYWRIGHT_AUTH_E2E_COOKIE,
  readSharedAuthTestScenarioFromDocumentCookie,
} from "../platform-auth/client"

import { getAuthProvider } from "../providers/auth"
import type { AppSupabaseClient } from "../providers/auth/types"

type BrowserSession = {
  access_token: string
  user: User
}

function getBrowserSupabaseEnvSnapshot() {
  return {
    NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
    NEXT_PUBLIC_SUPABASE_ANON_KEY: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
    NEXT_PUBLIC_SUPABASE_AUTH_URL: process.env.NEXT_PUBLIC_SUPABASE_AUTH_URL,
    NEXT_PUBLIC_SUPABASE_AUTH_ANON_KEY:
      process.env.NEXT_PUBLIC_SUPABASE_AUTH_ANON_KEY,
  }
}

export function hasConfiguredBrowserSupabaseEnv(
  env = getBrowserSupabaseEnvSnapshot()
) {
  const { url, anonKey } = getAuthProvider({ env }).getClientConfig()
  return Boolean(url) && Boolean(anonKey)
}

function getSharedAuthTestBrowserSession(): BrowserSession | null {
  const scenario = readSharedAuthTestScenarioFromDocumentCookie()
  if (!scenario) {
    return null
  }

  return {
    access_token: buildSharedAuthTestAccessToken(scenario),
    user: getSharedAuthTestUser(scenario) as User,
  }
}

export function hasPlaywrightAuthE2ESession() {
  return (
    isPlaywrightAuthE2EEnabled() && getSharedAuthTestBrowserSession() !== null
  )
}

function createPlaywrightAuthE2EClient() {
  return {
    auth: {
      async getSession() {
        const session = getSharedAuthTestBrowserSession()
        return {
          data: { session },
          error: null,
        }
      },
      async getUser() {
        const session = getSharedAuthTestBrowserSession()
        return {
          data: { user: session?.user ?? null },
          error: null,
        }
      },
      onAuthStateChange() {
        return {
          data: {
            subscription: {
              unsubscribe() {},
            },
          },
        }
      },
      async signOut() {
        if (typeof document !== "undefined") {
          document.cookie = `${PLAYWRIGHT_AUTH_E2E_COOKIE}=; Max-Age=0; path=/; SameSite=Lax`
        }

        return {
          error: null,
        }
      },
    },
  } as unknown as AppSupabaseClient
}

export function createClient(): AppSupabaseClient {
  if (hasPlaywrightAuthE2ESession()) {
    return createPlaywrightAuthE2EClient()
  }

  return getAuthProvider({
    env: getBrowserSupabaseEnvSnapshot(),
  }).createBrowserClient<AppSupabaseClient>()
}
